import React, { useState, useMemo } from "react";
import { Box, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import dayjs from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import { useCompanyBalanceSheet } from "../hooks/useCompanyBalanceSheet";
import { useStockCurrency } from "../../../../contexts/StockCurrencyContext";
import { formatNumber } from "../utils/formatNumber";
import { TransformedDataPoint } from "../utils/chartDataTransformers";
import {
  ViewMode,
  DEFAULT_QUARTERLY_LIMIT,
  MAX_QUARTERLY_LIMIT,
  MAX_ANNUAL_LIMIT,
  safeSlice,
  getApiPeriodParam,
} from "./shared/financialPeriodConfig";
import GenericFinancialBarChart from "./shared/GenericFinancialBarChart";
import ChartToggleButton from "./shared/ChartToggleButton";
import ChartTooltip from "./shared/ChartTooltip";

dayjs.extend(quarterOfYear);

const CASH_DEBT_CATEGORIES = [
  {
    key: "cashBalance",
    label: "Cash & Short-Term Investments",
    shortLabel: "Cash",
    color: "#22c55e",
    field: "cashAndShortTermInvestments",
  },
  {
    key: "totalDebt",
    label: "Total Debt",
    shortLabel: "Debt",
    color: "#ef4444",
    field: "totalDebt",
  },
] as const;

const categoryColors = CASH_DEBT_CATEGORIES.reduce(
  (acc, cat) => {
    acc[cat.key] = cat.color;
    return acc;
  },
  {} as Record<string, string>
);

interface CashDebtBreakdownChartProps {
  ticker: string;
  height?: number;
  isModal?: boolean;
  onCloseModal?: () => void;
  renderCard?: boolean;
  showFullscreen?: boolean;
  noHover?: boolean;
  viewMode?: ViewMode;
  limit?: number;
}

const CashDebtBreakdownChart: React.FC<CashDebtBreakdownChartProps> = ({
  ticker,
  height = 400,
  isModal = false,
  onCloseModal,
  renderCard = true,
  showFullscreen = false,
  noHover = false,
  viewMode: viewModeProp,
  limit: limitProp,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { currencySymbol } = useStockCurrency();

  const [internalViewMode] = useState<ViewMode>("quarterly");
  const [internalLimit] = useState(DEFAULT_QUARTERLY_LIMIT);

  const effectiveViewMode = viewModeProp ?? internalViewMode;
  const effectiveLimit = limitProp ?? internalLimit;

  const period = getApiPeriodParam(effectiveViewMode);
  const maxLimit = effectiveViewMode === "quarterly" ? MAX_QUARTERLY_LIMIT : MAX_ANNUAL_LIMIT;
  const { data, isLoading, error } = useCompanyBalanceSheet(
    ticker,
    period,
    maxLimit
  );

  const [visibleCategories, setVisibleCategories] = useState<Set<string>>(
    new Set(CASH_DEBT_CATEGORIES.map((cat) => cat.key))
  );

  const chartData = useMemo((): TransformedDataPoint[] => {
    if (!data || data.length === 0) return [];

    const validData = data.filter((item) =>
      item.cashAndShortTermInvestments !== null || item.totalDebt !== null
    );

    if (effectiveViewMode === "quarterly") {
      const sortedData = validData.sort(
        (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix()
      );

      const periodCounts = new Map<string, number>();

      const result = sortedData.map((item) => {
        const basePeriod = `Q${dayjs(item.date).quarter()} ${dayjs(item.date).year()}`;
        const count = periodCounts.get(basePeriod) ?? 0;
        periodCounts.set(basePeriod, count + 1);
        const periodLabel =
          count > 0 ? `${basePeriod} (${count + 1})` : basePeriod;

        return {
          period: periodLabel,
          cashBalance: (item.cashAndShortTermInvestments ?? 0) / 1000000,
          totalDebt: (item.totalDebt ?? 0) / 1000000,
          date: item.date,
          year: dayjs(item.date).year(),
          quarter: dayjs(item.date).quarter(),
        };
      });

      return safeSlice(result, effectiveLimit);
    }

    const result = validData
      .sort((a, b) => dayjs(a.date).unix() - dayjs(b.date).unix())
      .map((item) => ({
        period: dayjs(item.date).year().toString(),
        cashBalance: (item.cashAndShortTermInvestments ?? 0) / 1000000,
        totalDebt: (item.totalDebt ?? 0) / 1000000,
        date: item.date,
        year: dayjs(item.date).year(),
        quarter: null,
      }));

    return safeSlice(result, effectiveLimit);
  }, [data, effectiveViewMode, effectiveLimit]);

  const visibleKeys = CASH_DEBT_CATEGORIES
    .filter((cat) => visibleCategories.has(cat.key))
    .map((cat) => cat.key);

  const toggleCategory = (categoryKey: string) => {
    setVisibleCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryKey)) {
        newSet.delete(categoryKey);
      } else {
        newSet.add(categoryKey);
      }
      return newSet;
    });
  };

  const chipLegend = (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 1.5,
        mt: noHover ? 0 : 2,
        justifyContent: "center",
        px: noHover ? 0 : 2,
        ...(noHover && {
          maxWidth: "750px",
          mx: "auto",
        }),
      }}
    >
      {CASH_DEBT_CATEGORIES.map((category) => (
        <ChartToggleButton
          key={category.key}
          label={category.shortLabel}
          selected={visibleCategories.has(category.key)}
          onToggle={() => toggleCategory(category.key)}
          color={category.color}
        />
      ))}
    </Box>
  );

  return (
    <GenericFinancialBarChart
      title="Cash vs Debt"
      ticker={ticker}
      height={height}
      isModal={isModal}
      renderCard={renderCard}
      showFullscreen={showFullscreen}
      noHover={noHover}
      onCloseModal={onCloseModal}
      isLoading={isLoading}
      error={error}
      chartData={chartData}
      viewMode={effectiveViewMode}
      barKeys={visibleKeys}
      groupMode="grouped"
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      colors={({ id }: any) => categoryColors[id as string] ?? "#22c55e"}
      yAxisLegend="Amount"
      yAxisFormat={(value) =>
        `${currencySymbol}${formatNumber(value * 1000000)}`
      }
      valueLabelsFormat={(value) =>
        `${currencySymbol}${formatNumber(value * 1000000, 0)}`
      }
      noDataTitle="No Cash & Debt Data Available"
      noDataDescription="Cash and debt data is not available for this company, which is common for smaller companies, recent IPOs, or international stocks."
      tooltipContent={({ id, value, indexValue, color }) => {
        const category = CASH_DEBT_CATEGORIES.find((cat) => cat.key === id);
        return (
          <ChartTooltip
            header={String(indexValue)}
            headerColor={color}
            rows={[{ label: category?.label ?? "", value: `${currencySymbol}${formatNumber((value as number) * 1000000, 2)}` }]}
          />
        );
      }}
      belowChartContent={chipLegend}
    />
  );
};

export default CashDebtBreakdownChart;
